// src/components/common/FileUploader.tsx
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload, faTimesCircle, faFile, faDownload, faTrash, faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import {
  faFileAlt, faFilePdf, faFileWord, faFileImage, faFileVideo,
} from '@fortawesome/free-solid-svg-icons';
import clsx from 'clsx';
import {
  DocumentFilterDTO,
  getEntityDocuments,
  uploadDocument,
  deleteDocument,
  DocumentUploadResponse,
} from './UploadApi';

export type UploadStatus = 'ready' | 'uploading' | 'success' | 'error';

export interface UploadFile {
  file: File;
  previewUrl?: string;
  status: UploadStatus;
  errorMsg?: string;
}

interface FileUploaderProps {
  label?: string;
  accept?: string;
  multiple?: boolean;
  entityType: string;
  entityId: string;
  bucketName: string;
  metadata?: Record<string, string>;
  onFilesSelected?: (files: File[]) => void;
}

/** ---- Permission helpers ---- */
type PermissionDTO = {
  moduleId: string;
  moduleName: string;
  moduleDescription?: string;
  canCreate?: boolean;
  canView?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canReport?: boolean;

  canAssign?: boolean;
  canComment?: boolean;
  canUpload?: boolean;
  canApprove?: boolean;
  canReject?: boolean;
  canChangeStatus?: boolean;
  canArchive?: boolean;
  canRestore?: boolean;
  canExport?: boolean;
  canImport?: boolean;
  canShare?: boolean;

  visibleInSidebar?: boolean;
  visibleInDashboard?: boolean;
};

const getUserFromLS = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

const getModulePerm = (moduleName: string): PermissionDTO | undefined => {
  const u = getUserFromLS();
  const perms: PermissionDTO[] = u?.permissions || [];
  return perms.find((p) => (p.moduleName || '').toLowerCase() === moduleName.toLowerCase());
};

const hasAny = (perm: PermissionDTO | undefined, keys: (keyof PermissionDTO)[]) =>
  !!perm && keys.some((k) => !!(perm as any)[k]);

// IMPORTANT: We now gate uploads strictly by canCreate in the Uploads module.
const canCreateDocs = () => !!getModulePerm('Uploads')?.canCreate;
//console.log('canCreateDocs:', canCreateDocs());
const canViewDocs = () => hasAny(getModulePerm('Uploads'), ['canView', 'canCreate', 'canUpload']);
const canDeleteDocsOwn = () => hasAny(getModulePerm('Uploads'), ['canDelete', 'canEdit', 'canUpload']);
const canDeleteDocsAny = (roles?: string[]) => Array.isArray(roles) && roles.includes('Admin');

/** ---------------- Component ---------------- */
const FileUploader: React.FC<FileUploaderProps> = ({
  label = 'Upload Files',
  accept = '*/*',
  multiple = true,
  entityType,
  entityId,
  bucketName,
  metadata = {},
  onFilesSelected,
}) => {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [existingFiles, setExistingFiles] = useState<any[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  const user = getUserFromLS();
  const tenantId = user?.tenantId || '';
  const currentUserId = user?.id || '';
  const roles = user?.roles || [];
  const isAdmin = canDeleteDocsAny(roles);

  const _canCreate = canCreateDocs();  // <-- single source of truth for initiating upload
  const _canView = canViewDocs();
  const _canDeleteOwn = canDeleteDocsOwn();

  const fetchExistingDocs = useCallback(async () => {
    try {
      const filterDto: DocumentFilterDTO = {
        tenantId,
        entityType,
        entityId,
        bucketName,
      };
      const response = await getEntityDocuments(filterDto);
      setExistingFiles(response.data || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  }, [tenantId, entityType, entityId, bucketName]);

  useEffect(() => {
    fetchExistingDocs();
  }, [fetchExistingDocs]);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      // Block drag&drop completely if user lacks CREATE
      if (!_canCreate) return;
      event.preventDefault();
      const droppedFiles = Array.from(event.dataTransfer.files);
      processFiles(droppedFiles);
    },
    [_canCreate]
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Block file picker if user lacks CREATE
    if (!_canCreate) return;
    const selectedFiles = event.target.files ? Array.from(event.target.files) : [];
    processFiles(selectedFiles);
  };

  const processFiles = async (selectedFiles: File[]) => {
    if (!_canCreate) {
      alert('You do not have permission to upload documents.');
      return;
    }

    const tooLarge = selectedFiles.find((f) => f.size > 100 * 1024 * 1024);
    if (tooLarge) {
      alert(`"${tooLarge.name}" exceeds 100MB limit.`);
      return;
    }

    const queue: UploadFile[] = selectedFiles.map((file) => ({
      file,
      previewUrl: file.type.startsWith('image') ? URL.createObjectURL(file) : undefined,
      status: 'uploading',
    }));

    setFiles((prev) => [...prev, ...queue]);
    onFilesSelected?.(selectedFiles);

    try {
      const _responses: DocumentUploadResponse[] = await uploadDocument(
        tenantId,
        entityId,
        entityType,
        selectedFiles,
        bucketName
      );

      setFiles([]);
      await fetchExistingDocs(); // refresh from server
    } catch (err) {
      console.error('Upload failed:', err);
      setFiles((prev) =>
        prev.map((f) => ({ ...f, status: 'error', errorMsg: 'Upload failed' }))
      );
    }
  };

  const handleRemove = (index: number) => {
    const updated = [...files];
    updated.splice(index, 1);
    setFiles(updated);
  };

  const resolveDocOwnerId = (doc: any): string =>
    doc?.userId || doc?.uploadedBy || doc?.createdBy || '';

  const userOwnsDoc = (doc: any) => {
    const ownerId = resolveDocOwnerId(doc);
    return ownerId && currentUserId && ownerId === currentUserId;
  };

  const canDeleteThisDoc = (doc: any) => {
    if (isAdmin) return true;
    return _canDeleteOwn && userOwnsDoc(doc);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    const doc = existingFiles.find((d) => d.id === deleteId);
    if (!doc) {
      setDeleteId(null);
      return;
    }
    if (!canDeleteThisDoc(doc)) {
      alert('You do not have permission to delete this document.');
      setDeleteId(null);
      return;
    }

    try {
      await deleteDocument(deleteId);
      setDeleteId(null);
      await fetchExistingDocs();
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete document');
    }
  };

  const getFileIcon = (type: string) => {
    const t = (type || '').toUpperCase();
    if (t.includes('IMAGE')) return faFileImage;
    if (t.includes('VIDEO')) return faFileVideo;
    if (t.includes('PDF')) return faFilePdf;
    if (t.includes('WORD') || t.includes('DOC')) return faFileWord;
    return faFileAlt;
  };

  const renderPreview = (doc: any) => {
    if (doc.fileType?.startsWith('IMAGE') && doc.thumbnailUrl) {
      return (
        <a href={doc.previewUrl || doc.s3Url} target="_blank" rel="noopener noreferrer">
          <img
            src={doc.thumbnailUrl}
            alt="thumbnail"
            className="h-12 w-12 object-cover rounded shadow"
          />
        </a>
      );
    } else if (doc.fileType?.startsWith('VIDEO') && doc.thumbnailUrl) {
      return (
        <a href={doc.previewUrl || doc.s3Url} target="_blank" rel="noopener noreferrer">
          <img
            src={doc.thumbnailUrl}
            alt="video thumbnail"
            className="h-12 w-12 object-cover rounded shadow"
          />
        </a>
      );
    }
    return <span className="text-gray-400">N/A</span>;
  };

  const uploadDisabledReason = useMemo(() => {
    if (!_canCreate) return 'You do not have permission to upload documents.';
    return '';
  }, [_canCreate]);

  return (
    <div className="w-full p-4 border border-gray-300 rounded-xl shadow bg-white space-y-6">
      {/* Back Button */}
      <button
        className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
        onClick={() => navigate(-1)}
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        <span>Back</span>
      </button>

      {/* Metadata */}
      <div>
        <label className="block text-lg font-semibold mb-1">{label}</label>
        <div className="text-sm text-gray-600">
          Entity: <strong>{entityType}</strong> | ID: <strong>{entityId}</strong>
        </div>
        {metadata &&
          Object.entries(metadata).map(([key, val]) => (
            <div key={key} className="text-sm text-gray-500">
              {key}: <strong>{String(val)}</strong>
            </div>
          ))}
      </div>

      {/* Upload Area — now strictly gated by canCreate */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => (_canCreate ? inputRef.current?.click() : undefined)}
        className={clsx(
          'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors duration-300',
          _canCreate
            ? 'border-gray-400 hover:border-blue-500'
            : 'border-gray-200 bg-gray-50 cursor-not-allowed'
        )}
        title={uploadDisabledReason || undefined}
        role="button"
        aria-disabled={!_canCreate}
      >
        <FontAwesomeIcon
          icon={faUpload}
          className={clsx('text-3xl mb-2', _canCreate ? 'text-gray-500' : 'text-gray-300')}
        />
        <p className={clsx(_canCreate ? 'text-gray-600' : 'text-gray-400')}>
          {uploadDisabledReason || 'Drag & Drop files here or click to browse (max 100MB)'}
        </p>
        <input
          type="file"
          accept={accept}
          multiple={multiple}
          ref={inputRef}
          className="hidden"
          onChange={handleChange}
          disabled={!_canCreate}
        />
      </div>

      {/* Upload Queue */}
      {files.length > 0 && (
        <div className="space-y-3">
          {files.map((f, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between bg-gray-100 p-3 rounded-lg border"
            >
              <div className="flex items-center space-x-3">
                <FontAwesomeIcon icon={faFile} className="text-gray-600" />
                <div className="text-sm text-gray-800">{f.file.name}</div>
                {f.status === 'uploading' && (
                  <span className="text-yellow-500">Uploading...</span>
                )}
                {f.status === 'error' && <span className="text-red-600">Error</span>}
              </div>
              <button
                onClick={() => handleRemove(idx)}
                className="text-red-500 hover:text-red-700"
                title="Remove"
              >
                <FontAwesomeIcon icon={faTimesCircle} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Uploaded Files (gated by view permission) */}
      <div className="mt-6">
        <h3 className="text-md font-semibold mb-3">Uploaded Documents</h3>

        {!_canView ? (
          <div className="p-3 border rounded bg-gray-50 text-gray-600 text-sm">
            You don’t have permission to view documents.
          </div>
        ) : (
          <table className="w-full text-sm table-auto border border-gray-200 rounded overflow-hidden">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2">Preview</th>
                <th className="p-2">File Name</th>
                <th className="p-2">Type</th>
                <th className="p-2">Size</th>
                <th className="p-2">Uploaded By</th>
                <th className="p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {existingFiles.map((doc) => {
                const canDelete = canDeleteThisDoc(doc);
                const ownerId = resolveDocOwnerId(doc);
                const uploaderName = doc?.username || doc?.uploadedByName || ownerId || '—';

                return (
                  <tr key={doc.id} className="border-t">
                    <td className="p-2">{renderPreview(doc)}</td>
                    <td className="p-2 flex items-center space-x-2">
                      <FontAwesomeIcon icon={getFileIcon(doc.fileType || '')} className="text-gray-600" />
                      <span>{doc.fileName}</span>
                    </td>
                    <td className="p-2">{doc.fileType}</td>
                    <td className="p-2">{(doc.size / 1024).toFixed(2)} KB</td>
                    <td className="p-2">{uploaderName}</td>
                    <td className="p-2 flex space-x-3">
                      {_canView && doc.s3Url && (
                        <a href={doc.s3Url} download className="text-blue-500" target="_blank" title="Download">
                          <FontAwesomeIcon icon={faDownload} />
                        </a>
                      )}
                      <button
                        onClick={() => (canDelete ? setDeleteId(doc.id) : undefined)}
                        className={clsx(
                          'text-red-500',
                          canDelete ? 'hover:text-red-700' : 'opacity-40 cursor-not-allowed'
                        )}
                        title={
                          canDelete
                            ? 'Delete'
                            : userOwnsDoc(doc)
                            ? 'You lack delete permission'
                            : 'You can only delete your own uploads'
                        }
                        disabled={!canDelete}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!existingFiles.length && (
                <tr>
                  <td colSpan={6} className="text-center p-4 text-gray-400">
                    No documents found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete Confirmation */}
      {deleteId && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg space-y-4 w-96">
            <h2 className="text-lg font-semibold text-red-600">Confirm Delete</h2>
            <p>Are you sure you want to delete this document?</p>
            <div className="flex justify-end space-x-4">
              <button onClick={() => setDeleteId(null)} className="text-gray-600">
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="text-white bg-red-600 hover:bg-red-700 px-4 py-1 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploader;